import { useState } from "react";

import { IWardMeasurementType } from "../../../@types/admin/AdminMeasurementType";
import {
  useAddWardMeasurementType,
  useUpdateWardMeasurementType,
} from "../../../hooks/mutations/admin/measurementTypes";
import { useAvailableMeasurementTypes } from "../../../hooks/queries/admin/measurementTypes";
import { useToast } from "../../../hooks/useToast";
import {
  errorToastMessage,
  serverErrorMessage,
} from "../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  ReadOnlyValue,
  WarningNotice,
} from "../shared";

interface IWardMeasurementTypeModalProps {
  wardId: number;
  wardName: string;
  /** Omitted for an add. */
  measurementType?: IWardMeasurementType;
  /** True when the ward has no configuration yet — adding one cuts it over. */
  isWardUnconfigured: boolean;
  onClose: () => void;
}

/**
 * Add or edit one of a ward's measurement types — Retool's
 * `modalAddEditMeasurementType` (EN-5157), which used one form for both,
 * hiding the type picker in edit mode.
 *
 * The form edits display flags only. That is the whole of what the API
 * exposes per row (`UpdateWardMeasurementTypeRequestDto`: displayInPortal,
 * displayInApp, order) — the value thresholds live on the catalogue entry and
 * are seeded out of band, so there is no threshold editor here despite what
 * the ticket anticipated.
 */
export function WardMeasurementTypeModal({
  wardId,
  wardName,
  measurementType,
  isWardUnconfigured,
  onClose,
}: IWardMeasurementTypeModalProps) {
  const isEdit = Boolean(measurementType);
  const { showToast } = useToast();
  const { availableMeasurementTypes, isLoading: catalogLoading } =
    useAvailableMeasurementTypes(isEdit ? undefined : wardId);
  const { addWardMeasurementType, isAdding } =
    useAddWardMeasurementType(wardId);
  const { updateWardMeasurementType, isUpdating } =
    useUpdateWardMeasurementType(wardId);

  const [code, setCode] = useState(measurementType?.measurementTypeCode ?? "");
  // Retool's switches started off for a new row. The API defaults both to
  // true, and a type shown in neither surface is invisible to everyone, so a
  // new row starts visible and the operator turns one off deliberately.
  const [displayInPortal, setDisplayInPortal] = useState(
    measurementType?.displayInPortal ?? true
  );
  const [displayInApp, setDisplayInApp] = useState(
    measurementType?.displayInApp ?? true
  );
  const [touched, setTouched] = useState(false);
  /**
   * The server's reason for refusing the write, shown in the dialog rather
   * than as a toast: it names what to change first, which is too much to read
   * before a toast times out, and the dialog stays open so the operator can
   * put the toggle back.
   */
  const [rejection, setRejection] = useState<string | undefined>();

  const isSaving = isAdding || isUpdating;
  const codeError = isEdit || code ? undefined : "Select a measurement type.";
  const shownCodeError = touched ? codeError : undefined;

  const handleSave = async () => {
    if (codeError) return;

    setRejection(undefined);
    try {
      if (measurementType) {
        await updateWardMeasurementType({
          code: measurementType.measurementTypeCode,
          request: { displayInPortal, displayInApp },
        });
        showToast("The measurement type was updated successfully.", "success");
      } else {
        await addWardMeasurementType({
          measurementTypeCode: code,
          displayInPortal,
          displayInApp,
        });
        showToast("The measurement type was added successfully.", "success");
      }
      onClose();
    } catch (error) {
      const refused = serverErrorMessage(error);
      if (refused) {
        setRejection(refused);
        return;
      }
      showToast(
        errorToastMessage(
          error,
          isEdit
            ? "There was an error updating this measurement type."
            : "There was an error adding this measurement type."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>
        {isEdit ? "Edit measurement type" : "Add measurement type"}
      </ModalTitle>

      <ModalForm>
        {rejection && <WarningNotice>{rejection}</WarningNotice>}

        {!isEdit && isWardUnconfigured && (
          <WarningNotice>
            {wardName} has no measurement types configured. Adding the first one
            switches the ward over: every patient in it will then take its
            measurement types from this list instead of from their conditions.
          </WarningNotice>
        )}

        <FilterGroup>
          <FilterLabel htmlFor="ward-measurement-type">
            Measurement type
          </FilterLabel>
          {isEdit ? (
            <ReadOnlyValue id="ward-measurement-type">
              {measurementType?.measurementTypeDisplayName} -{" "}
              {measurementType?.measurementTypeCode}
            </ReadOnlyValue>
          ) : (
            <AdminSelectField
              id="ward-measurement-type"
              value={code}
              disabled={catalogLoading}
              onBlur={() => setTouched(true)}
              onChange={(e) => {
                setTouched(true);
                setCode(e.target.value);
              }}
            >
              <option value="">
                {catalogLoading ? "Loading..." : "Select an option"}
              </option>
              {availableMeasurementTypes.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.displayName} - {item.code}
                </option>
              ))}
            </AdminSelectField>
          )}
          {!isEdit &&
            !catalogLoading &&
            availableMeasurementTypes.length === 0 && (
              <FieldHint>
                Every catalogue measurement type is already on this ward.
              </FieldHint>
            )}
          {shownCodeError && <FieldError>{shownCodeError}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Visible in</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={displayInPortal}
              onChange={(e) => setDisplayInPortal(e.target.checked)}
            />
            Portal
          </CheckboxField>
          <CheckboxField>
            <input
              type="checkbox"
              checked={displayInApp}
              onChange={(e) => setDisplayInApp(e.target.checked)}
            />
            App
          </CheckboxField>
          {!displayInPortal && !displayInApp && (
            <FieldHint>
              With both off the type stays configured but nobody sees it.
            </FieldHint>
          )}
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSaving || Boolean(codeError)}
            onClick={handleSave}
          >
            {(() => {
              if (isSaving) return isEdit ? "Saving..." : "Adding...";
              return isEdit ? "Save" : "Add";
            })()}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
